import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  FEED_TEMPLATE_TYPE_STORAGE_KEY,
  SDK_TEMPLATE_TYPE_STORAGE_KEY,
} from "../constants/template";
import { FeedTemplateType, TemplateType } from "../types/template";
import {
  getTemplateTypeFromRouteContext,
  normalizeTemplateType,
  normalizeFeedTemplateType,
} from "../utils/templateRouting";

export const useTemplateRouting = () => {
  const location = useLocation();

  const selectedFeedTemplateFromState = useMemo(() => {
    const state = location.state as { templateType?: string } | null;
    return normalizeFeedTemplateType(state?.templateType || null);
  }, [location.state]);
  const selectedTemplateFromState = useMemo<TemplateType | null>(() => {
    const state = location.state as
      | { templateType?: string; sdkTemplateType?: string }
      | null;
    return normalizeTemplateType(
      state?.sdkTemplateType || state?.templateType || null
    );
  }, [location.state]);

  const selectedFeedTemplateType = useMemo<FeedTemplateType>(() => {
    if (selectedFeedTemplateFromState) {
      return selectedFeedTemplateFromState;
    }

    return (
      normalizeFeedTemplateType(
        localStorage.getItem(FEED_TEMPLATE_TYPE_STORAGE_KEY)
      ) || "VERTICAL_VIDEO"
    );
  }, [selectedFeedTemplateFromState]);

  useEffect(() => {
    if (!selectedFeedTemplateFromState) return;

    localStorage.setItem(
      FEED_TEMPLATE_TYPE_STORAGE_KEY,
      selectedFeedTemplateFromState
    );
  }, [selectedFeedTemplateFromState]);

  useEffect(() => {
    if (!selectedTemplateFromState) return;
    localStorage.setItem(SDK_TEMPLATE_TYPE_STORAGE_KEY, selectedTemplateFromState);
  }, [selectedTemplateFromState]);

  const selectedTemplateType = useMemo(
    () =>
      getTemplateTypeFromRouteContext(
        location.pathname,
        selectedFeedTemplateType
      ),
    [location.pathname, selectedFeedTemplateType]
  );
  const selectedSdkTemplateType = useMemo<TemplateType | null>(() => {
    if (selectedTemplateFromState) {
      return selectedTemplateFromState;
    }

    const selectedTemplateFromStorage = normalizeTemplateType(
      localStorage.getItem(SDK_TEMPLATE_TYPE_STORAGE_KEY)
    );
    if (selectedTemplateFromStorage) {
      return selectedTemplateFromStorage;
    }

    return selectedTemplateType;
  }, [selectedTemplateFromState, selectedTemplateType]);

  return {
    pathname: location.pathname,
    search: location.search,
    selectedFeedTemplateType,
    selectedTemplateType,
    selectedSdkTemplateType,
  };
};
